import React, { useEffect, useRef } from 'react';
import AgentTraceBlock from './AgentTraceBlock';
import AgentPlanThinking from './AgentPlanThinking';
import AgentPlanCard from './AgentPlanCard';
import AgentErrorBlock from './AgentErrorBlock';
import AgentMentionInput from './AgentMentionInput';
import { useAgent } from '../../context/AgentContext';

/**
 * AgentChat — center conversation thread panel.
 * Renders messages, trace blocks, plan cards, error blocks, and the input.
 *
 * Props:
 *   pageContext — {page, client_id, portfolio_id} for system prompt
 */
const AgentChat = ({ pageContext }) => {
  const {
    messages,
    traceSteps,
    streamingState,
    taskBreakdown,
    taskStatuses,
    sendMessage,
    confirmPlan,
    cancelPlan,
    retryStep,
    skipStep,
    undoSession,
  } = useAgent();

  const bottomRef = useRef(null);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, traceSteps]);

  const isInputDisabled =
    streamingState === 'thinking' ||
    streamingState === 'executing' ||
    streamingState === 'awaiting_confirmation';

  const disabledLabel =
    streamingState === 'thinking' ? 'Agent is thinking…' :
    streamingState === 'executing' ? 'Agent is working…' :
    streamingState === 'awaiting_confirmation' ? 'Confirm or cancel the plan above' : undefined;

  const handleSend = (content, mentions, attachments) => {
    sendMessage(content, mentions, attachments, pageContext);
  };

  const handleConfirm = (selectedStepIndices) => {
    confirmPlan(selectedStepIndices);
  };

  const handleCancel = () => {
    cancelPlan();
  };

  const handlePageReload = () => {
    window.location.reload();
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages area */}
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.length === 0 && streamingState === 'idle' && (
          <div className="flex items-center justify-center h-full">
            <p className="text-sm text-white/50 text-center px-6">
              Ask anything about your collections…
            </p>
          </div>
        )}

        {messages.map((msg) => {
          if (msg.role === 'user') {
            return (
              <div key={msg.id} className="flex justify-end">
                <div className="max-w-[85%] bg-white rounded-2xl rounded-tr-sm px-3 py-2 text-sm text-gray-900">
                  {msg.content}
                </div>
              </div>
            );
          }

          if (msg.type === 'text') {
            return (
              <div key={msg.id} className="flex items-start gap-2">
                <VasariDot />
                <div className="flex-1 min-w-0">
                  <AgentMarkdown text={msg.content} />
                </div>
              </div>
            );
          }

          if (msg.type === 'plan') {
            return (
              <div key={msg.id} className="flex items-start gap-2">
                <VasariDot />
                <div className="flex-1 min-w-0">
                  <AgentPlanCard
                    plan={msg.plan}
                    onConfirm={handleConfirm}
                    onCancel={handleCancel}
                    locked={streamingState === 'executing'}
                    cancelled={msg.cancelled === true}
                    executed={msg.executed === true}
                  />
                </div>
              </div>
            );
          }

          if (msg.type === 'error') {
            return (
              <div key={msg.id} className="flex items-start gap-2">
                <VasariDot />
                <div className="flex-1 min-w-0">
                  <AgentErrorBlock
                    message={msg}
                    onRetry={() => retryStep(msg.completed_steps)}
                    onSkip={() => skipStep(msg.completed_steps)}
                    onUndo={undoSession}
                  />
                </div>
              </div>
            );
          }

          if (msg.type === 'session_complete') {
            return (
              <div key={msg.id} className="flex items-start gap-2">
                <VasariDot />
                <div className="flex-1 min-w-0">
                  <SessionCompleteBlock
                    changes={msg.changes}
                    affectedResources={msg.affected_resources}
                    reportUrls={msg.report_urls || []}
                    onReload={handlePageReload}
                    onUndo={undoSession}
                    pageContext={pageContext}
                  />
                </div>
              </div>
            );
          }

          return null;
        })}

        {/* Task breakdown (hierarchical plan thinking view) or fallback trace block */}
        {taskBreakdown ? (
          <AgentPlanThinking
            tasks={taskBreakdown}
            taskStatuses={taskStatuses}
          />
        ) : (
          (streamingState === 'thinking' || streamingState === 'executing' || traceSteps.length > 0) && (
            <AgentTraceBlock
              steps={traceSteps}
              isThinking={streamingState === 'thinking'}
            />
          )
        )}

        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <AgentMentionInput
        onSend={handleSend}
        disabled={isInputDisabled}
        disabledLabel={disabledLabel}
        placeholder={messages.length === 0 ? 'Ask anything about your collections…' : undefined}
      />
    </div>
  );
};

const VasariDot = () => (
  <div className="w-4 h-4 rounded-full bg-white/20 flex-shrink-0 mt-0.5" />
);

/**
 * Renders agent message text with basic markdown formatting:
 * **bold**, numbered lists (1. 2. 3.), bullet lists (- *), paragraph breaks.
 */
const AgentMarkdown = ({ text }) => {
  if (!text) return null;

  const renderInline = (str) => {
    const parts = str.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return <strong key={i} className="font-semibold text-white">{part.slice(2, -2)}</strong>;
      }
      return part;
    });
  };

  const lines = text.split('\n');
  const blocks = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];
    const numberedMatch = line.match(/^(\d+)\.\s+(.+)/);
    const bulletMatch = line.match(/^[-*]\s+(.+)/);

    if (numberedMatch) {
      const items = [];
      while (i < lines.length && lines[i].match(/^\d+\.\s+(.+)/)) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', items });
    } else if (bulletMatch) {
      const items = [];
      while (i < lines.length && lines[i].match(/^[-*]\s+(.+)/)) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', items });
    } else if (line.trim() === '') {
      blocks.push({ type: 'spacer' });
      i++;
    } else {
      blocks.push({ type: 'p', text: line });
      i++;
    }
  }

  return (
    <div className="text-sm text-white leading-relaxed">
      {blocks.map((block, idx) => {
        if (block.type === 'spacer') return <div key={idx} className="h-1" />;
        if (block.type === 'p') return <p key={idx}>{renderInline(block.text)}</p>;
        if (block.type === 'ol') {
          return (
            <ol key={idx} className="mt-1 space-y-0.5">
              {block.items.map((item, j) => (
                <li key={j} className="flex gap-2">
                  <span className="text-white/40 flex-shrink-0 select-none">{j + 1}.</span>
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ol>
          );
        }
        if (block.type === 'ul') {
          return (
            <ul key={idx} className="mt-1 space-y-0.5">
              {block.items.map((item, j) => (
                <li key={j} className="flex gap-2">
                  <span className="text-white/40 flex-shrink-0 select-none">•</span>
                  <span>{renderInline(item)}</span>
                </li>
              ))}
            </ul>
          );
        }
        return null;
      })}
    </div>
  );
};

const REPORT_TYPE_LABELS = {
  collection_summary: 'Collection Summary',
  insurance_schedule: 'Insurance Schedule',
  artwork_report: 'Artwork Report',
};

const SessionCompleteBlock = ({ changes, affectedResources, reportUrls = [], onReload, onUndo, pageContext }) => {
  // Determine if current page is affected
  const isAffected = affectedResources?.some((r) => {
    if (!pageContext) return false;
    if (r.startsWith('portfolio_id:') && pageContext.portfolio_id) {
      return r === `portfolio_id:${pageContext.portfolio_id}`;
    }
    if (r.startsWith('client_id:') && pageContext.client_id) {
      return r === `client_id:${pageContext.client_id}`;
    }
    return false;
  });

  return (
    <div className="border border-white/20 rounded-lg px-4 py-3 bg-white/10">
      <div className="flex items-center gap-2 mb-1">
        <div className="w-2 h-2 rounded-full bg-white/40" />
        <span className="text-sm font-medium text-white/80">Session complete</span>
      </div>
      {changes?.length > 0 && (
        <p className="text-xs text-white/50 mb-2">{changes.join(' · ')}</p>
      )}

      {/* Report download links */}
      {reportUrls.length > 0 && (
        <div className="flex flex-col gap-1 mb-2">
          {reportUrls.map((r, i) => (
            <a
              key={i}
              href={r.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-medium text-white/70 border border-white/30 rounded px-2 py-1 hover:bg-white/10 transition-colors w-fit"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download {REPORT_TYPE_LABELS[r.report_type] || r.report_type} PDF
            </a>
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-wrap">
        {isAffected && (
          <button
            onClick={onReload}
            className="text-xs font-medium text-white border border-white/30 rounded px-2 py-1 hover:bg-white/20 transition-colors"
          >
            Data was updated — click to reload this page
          </button>
        )}
        <button
          onClick={onUndo}
          className="text-xs text-white/40 hover:text-white/70 border border-white/20 rounded px-2 py-1 hover:bg-white/10 transition-colors"
        >
          Undo (10 min window)
        </button>
      </div>
    </div>
  );
};

export default AgentChat;
